import { useEffect, useMemo, useState, useCallback } from 'react';
import { Platform } from 'react-native';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { getDeployTarget } from '../config/environment';
import {
  subscribeUserFunds,
  subscribeFundsHistory,
  subscribeActiveMutualFunds,
  setBillingReserve,
  donateToMutualFund,
  transferFundsToUser,
  findUserByEmailForTransfer,
  hasPeerWalletSendToRecipient,
  ensureMainMutualFund,
} from '../utils/userFundsService';
import {
  formatFundsCents,
  clampBillingReserveCents,
} from '../constants/userFunds';
import { PLAN_IDS, SUBSCRIPTION_PLANS } from '../constants/subscriptionPlans';
import { useCreditEconomyOptional } from '../contexts/CreditEconomyContext';

export function useUserFunds(uid) {
  const [balanceCents, setBalanceCents] = useState(0);
  const [reservedForBillingCents, setReservedForBillingCents] = useState(0);
  const [payoutBalanceCents, setPayoutBalanceCents] = useState(0);
  const [payoutBalanceReferralHoldCents, setPayoutBalanceReferralHoldCents] = useState(0);
  const [payoutWithdrawnCents, setPayoutWithdrawnCents] = useState(0);
  const [history, setHistory] = useState([]);
  const [mutualFunds, setMutualFunds] = useState([]);
  const [loading, setLoading] = useState(Boolean(uid));
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState(null);
  const creditEconomy = useCreditEconomyOptional();
  const maxBillingApplyCents = creditEconomy.maxSubscriptionDiscountCents(
    SUBSCRIPTION_PLANS[PLAN_IDS.VRC_MEMBER].priceCents,
  );

  useEffect(() => {
    if (!uid) {
      setBalanceCents(0);
      setReservedForBillingCents(0);
      setPayoutBalanceCents(0);
      setPayoutBalanceReferralHoldCents(0);
      setPayoutWithdrawnCents(0);
      setHistory([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const unsubUser = subscribeUserFunds(
      uid,
      (data) => {
        setBalanceCents(data.balanceCents);
        setReservedForBillingCents(data.reservedForBillingCents);
        setPayoutBalanceCents(data.payoutBalanceCents || 0);
        setPayoutBalanceReferralHoldCents(data.payoutBalanceReferralHoldCents || 0);
        setPayoutWithdrawnCents(data.payoutWithdrawnCents || 0);
        setLoading(false);
      },
      (err) => {
        console.warn('funds user subscribe', err);
        setError(err?.message || 'Could not load funds');
        setLoading(false);
      },
    );
    const unsubHist = subscribeFundsHistory(
      uid,
      setHistory,
      (err) => console.warn('funds history subscribe', err),
    );
    return () => {
      unsubUser?.();
      unsubHist?.();
    };
  }, [uid]);

  useEffect(() => {
    if (!uid) return undefined;
    ensureMainMutualFund().catch((err) =>
      console.warn('ensureMainMutualFund', err)
    );
    return undefined;
  }, [uid]);

  useEffect(() => {
    const unsub = subscribeActiveMutualFunds(
      setMutualFunds,
      (err) => {
        console.warn('mutual funds subscribe', err);
      },
    );
    return () => {
      unsub?.();
    };
  }, []);

  // Credits balance (subscription-only). Wallet donate uses payoutBalanceCents.
  const availableCents = Math.max(0, balanceCents);
  const donateAvailableCents = Math.max(0, payoutBalanceCents);

  const reserveForBilling = useCallback(async (amountCents) => {
    if (!uid) throw new Error('Sign in to use funds');
    setActionBusy(true);
    setError(null);
    try {
      const capped = clampBillingReserveCents(amountCents, balanceCents, maxBillingApplyCents);
      const result = await setBillingReserve({ uid, amountCents: capped });
      setReservedForBillingCents(result.reservedForBillingCents);
      return result;
    } catch (err) {
      setError(err?.message || 'Could not update billing reserve');
      throw err;
    } finally {
      setActionBusy(false);
    }
  }, [uid, balanceCents, maxBillingApplyCents]);

  const donate = useCallback(async (fundId, amountCents) => {
    if (!uid) throw new Error('Sign in to donate');
    setActionBusy(true);
    setError(null);
    try {
      return await donateToMutualFund({ uid, fundId, amountCents });
    } catch (err) {
      setError(err?.message || 'Donation failed');
      throw err;
    } finally {
      setActionBusy(false);
    }
  }, [uid]);

  /**
   * Donate with credits and/or card.
   * Returns { completed, url?, creditCents, cardCents }.
   * If url is set, caller should redirect to Stripe Checkout.
   */
  const donateMixed = useCallback(async (fundId, {
    creditCents = 0,
    cardCents = 0,
    successUrl,
    cancelUrl,
  } = {}) => {
    if (!uid) throw new Error('Sign in to donate');
    const credits = Math.max(0, Math.round(Number(creditCents) || 0));
    const card = Math.max(0, Math.round(Number(cardCents) || 0));
    if (credits + card < 1) throw new Error('Enter a donation amount');

    // Credits-only: keep existing client transaction (no Stripe).
    if (card < 1) {
      setActionBusy(true);
      setError(null);
      try {
        await donateToMutualFund({ uid, fundId, amountCents: credits });
        return { completed: true, creditCents: credits, cardCents: 0 };
      } catch (err) {
        setError(err?.message || 'Donation failed');
        throw err;
      } finally {
        setActionBusy(false);
      }
    }

    setActionBusy(true);
    setError(null);
    try {
      await ensureMainMutualFund();
      const origin =
        Platform.OS === 'web' && typeof window !== 'undefined' && window.location?.origin
          ? window.location.origin
          : null;
      const createMutualFundDonationCheckout = httpsCallable(
        functions,
        'createMutualFundDonationCheckout',
        { timeout: 60000 }
      );
      const result = await createMutualFundDonationCheckout({
        fundId,
        creditCents: credits,
        cardCents: card,
        deployTarget: getDeployTarget(),
        ...(origin
          ? {
              successUrl: successUrl || `${origin}/fund?donate=success`,
              cancelUrl: cancelUrl || `${origin}/fund?donate=cancelled`,
            }
          : {}),
      });
      return result?.data || {};
    } catch (err) {
      setError(err?.message || 'Donation checkout failed');
      throw err;
    } finally {
      setActionBusy(false);
    }
  }, [uid]);

  const transferToUser = useCallback(async (toUid, amountCents, toLabel) => {
    if (!uid) throw new Error('Sign in to transfer');
    setActionBusy(true);
    setError(null);
    try {
      return await transferFundsToUser({
        fromUid: uid,
        toUid,
        amountCents,
        toLabel,
      });
    } catch (err) {
      setError(err?.message || 'Transfer failed');
      throw err;
    } finally {
      setActionBusy(false);
    }
  }, [uid]);

  const lookupUserByEmail = useCallback(async (email) => {
    return findUserByEmailForTransfer(email);
  }, []);

  const hasSentWalletTo = useCallback(async (toUid) => {
    if (!uid || !toUid) return false;
    return hasPeerWalletSendToRecipient(uid, toUid);
  }, [uid]);

  const summary = useMemo(() => ({
    balanceLabel: formatFundsCents(balanceCents),
    availableLabel: formatFundsCents(availableCents),
    reservedLabel: formatFundsCents(reservedForBillingCents),
    maxBillingLabel: formatFundsCents(maxBillingApplyCents),
  }), [balanceCents, availableCents, reservedForBillingCents, maxBillingApplyCents]);

  return {
    loading,
    actionBusy,
    error,
    balanceCents,
    availableCents,
    donateAvailableCents,
    reservedForBillingCents,
    payoutBalanceCents,
    payoutBalanceReferralHoldCents,
    payoutWithdrawnCents,
    history,
    mutualFunds,
    summary,
    reserveForBilling,
    donate,
    donateMixed,
    transferToUser,
    lookupUserByEmail,
    hasSentWalletTo,
    maxBillingApplyCents,
  };
}
